import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Users, Search, Filter, Heart, Eye, Calendar, Phone, AlertTriangle, CheckCircle, User, Baby, School, Activity, Utensils, Shield, Clock, X, MapPin, Mail, Building } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { NiveauDAcces } from '../NiveauDAcces'
import type { NiveauAcces } from '../../../lib/inclusion/demandes'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CenterKidsProps {
  initialParams?: {
    centerId?: string
  } | null
  onParamsConsumed?: () => void
}

interface Center {
  id: string
  name: string
}

interface Kid {
  id: string
  nom: string
  prenom: string
  date_naissance: string
  n_national?: string
  photo_url?: string
  photo_signed_url?: string
  adresse?: string
  cpostal?: string
  localite?: string
  ecole?: string
  school?: {
    id: string
    name: string
  }
  is_national_number_valid: boolean
  photo_consent: boolean
  created_at: string
  user_id: string
  user?: {
    nom?: string
    prenom?: string
    email: string
    telephone?: string
  }
  health?: {
    specific_medical?: string
    medication: boolean
    medication_details?: string
    tetanus: boolean
    doctor_name?: string
    doctor_phone?: string
  }
  allergies?: {
    has_allergies: boolean
    allergies_details?: string
    special_diet: boolean
    diet_details?: string
  }
  activities?: {
    can_participate: boolean
    can_swim: boolean
    swim_level?: string
    restriction_details?: string
  }
  departure?: {
    leaves_alone: boolean
    departure_time?: string
  }
  inclusion?: {
    id?: string
    has_needs: boolean
    situation_details?: string
    impact_details?: string
    needs_dedicated_staff?: string
    staff_details?: string
    strategies?: string
    assistive_devices?: string
    stress_signals?: string
    strengths?: string
    previous_experience?: string
    niveau_acces?: NiveauAcces | null
    niveau_acces_le?: string | null
    niveau_acces_motif?: string | null
  }
  authorized_persons?: {
    id: string
    first_name: string
    last_name: string
    phone_number: string
    relationship: string
    photo_url?: string
  }[]
  registrations_count?: number
  active_registrations_count?: number
}

interface SessionGroup {
  label: string
  periode: string
  semaine: string
  startDate: string
  endDate: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isNoIssueText(text?: string): boolean {
  if (!text) return true
  const normalized = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,;:!?\-_/\\'"()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (normalized === '') return true
  const noIssuePhrases = [
    'tout va bien', 'tout est ok', 'tout est bon', 'tout ok', 'ok',
    'rien a signaler', 'rien de particulier', 'rien de special', 'rien',
    'ras', 'r a s', 'neant', 'aucun', 'aucune', 'aucun probleme',
    'pas de probleme', 'pas de soucis', 'pas de souci', 'pas de traitement',
    'non', 'n a', 'na', 'nc', 'non concerne', 'non concernee',
    'sans objet', 'sans', 'vide', 'x', '-', 'no', 'none',
    'everything is fine', 'all good', 'nothing', 'nothing to report',
    'no issues', 'no problem', 'no problems', 'not applicable',
    'n/a', 'nil', 'healthy', 'good', 'fine', 'normal'
  ]
  return noIssuePhrases.includes(normalized)
}

function hasMedicalInfo(kid: Kid): boolean {
  return kid.health?.medication === true || !isNoIssueText(kid.health?.specific_medical)
}

function calculateAge(birthDate: string): number {
  const today = new Date()
  const birth = new Date(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR')
}

function getHealthStatus(kid: Kid): string[] {
  const issues: string[] = []
  if (!isNoIssueText(kid.health?.specific_medical)) issues.push('Problèmes médicaux')
  if (kid.health?.medication) issues.push('Médicaments')
  if (kid.allergies?.has_allergies) issues.push('Allergies')
  if (kid.allergies?.special_diet) issues.push('Régime spécial')
  if (kid.inclusion?.has_needs) issues.push('Besoins spéciaux')
  return issues
}

const KID_SELECT = `
  *,
  users!kids_user_id_fkey(id, nom, prenom, email, telephone),
  health:kid_health(*),
  allergies:kid_allergies(*),
  activities:kid_activities(*),
  departure:kid_departure(*),
  inclusion:kid_inclusion(id, has_needs, situation_details, impact_details, needs_dedicated_staff, staff_details, strategies, assistive_devices, stress_signals, strengths, previous_experience, niveau_acces, niveau_acces_le, niveau_acces_motif),
  registrations(id, payment_status)
`

function mapKidRow(kid: any): Kid {
  return {
    ...kid,
    user: kid.users,
    registrations_count: kid.registrations?.length || 0,
    active_registrations_count: kid.registrations?.filter((r: any) => r.payment_status !== 'cancelled').length || 0,
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

async function resolveSchoolNames(kids: Kid[]): Promise<void> {
  const uuids = [...new Set(kids.map(k => k.ecole).filter((e): e is string => !!e && UUID_RE.test(e)))]
  if (uuids.length === 0) return
  const map = new Map<string, string>()
  for (let i = 0; i < uuids.length; i += 50) {
    const batch = uuids.slice(i, i + 50)
    const { data } = await supabase.from('schools').select('id, name').in('id', batch)
    for (const s of data || []) map.set(s.id, s.name)
  }
  for (const kid of kids) {
    if (kid.ecole && map.has(kid.ecole)) kid.ecole = map.get(kid.ecole)!
  }
}

async function generatePhotoSignedUrls(kids: Kid[]): Promise<void> {
  const withPhoto = kids.filter(k => k.photo_url)
  if (withPhoto.length === 0) return
  const paths = withPhoto.map(k => k.photo_url!)
  const { data } = await supabase.storage.from('kid-photos').createSignedUrls(paths, 3600)
  if (!data) return
  for (let i = 0; i < withPhoto.length; i++) {
    if (data[i]?.signedUrl) withPhoto[i].photo_signed_url = data[i].signedUrl
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CenterKidsManagement({ initialParams, onParamsConsumed }: CenterKidsProps) {
  // Base kids data
  const [baseKids, setBaseKids] = useState<Kid[]>([])
  const [loading, setLoading] = useState(false)

  // Center selection
  const [centers, setCenters] = useState<Center[]>([])
  const [selectedCenter, setSelectedCenter] = useState('')

  // Session groups
  const [sessionGroups, setSessionGroups] = useState<SessionGroup[]>([])
  const [selectedSessionGroupIndex, setSelectedSessionGroupIndex] = useState<number>(-1)

  // Client-side search (no DB query)
  const [searchQuery, setSearchQuery] = useState('')

  // Detail modal
  const [selectedKid, setSelectedKid] = useState<Kid | null>(null)

  // Filters UI
  const [showFilters, setShowFilters] = useState(false)

  // Client-side filters
  const [hasInclusionNeeds, setHasInclusionNeeds] = useState(false)
  const [schoolFilter, setSchoolFilter] = useState('')

  // -----------------------------------------------------------------------
  // Initial load: fetch centers
  // -----------------------------------------------------------------------
  useEffect(() => {
    const fetchCenters = async () => {
      const { data } = await supabase
        .from('centers')
        .select('id, name')
        .eq('active', true)
        .order('name')
      setCenters(data || [])
    }
    fetchCenters()
  }, [])

  // -----------------------------------------------------------------------
  // Handle initialParams: auto-select center
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (initialParams?.centerId && centers.length > 0) {
      const match = centers.find(c => c.id === initialParams.centerId)
      if (match) {
        setSelectedCenter(match.id)
      }
      onParamsConsumed?.()
    }
  }, [initialParams, centers])

  // -----------------------------------------------------------------------
  // When center changes: fetch sessions (current + upcoming 7 days)
  // -----------------------------------------------------------------------
  useEffect(() => {
    setSessionGroups([])
    setSelectedSessionGroupIndex(-1)
    setBaseKids([])

    if (!selectedCenter) return

    const fetchSessions = async () => {
      const today = new Date()
      const todayStr = today.toISOString().slice(0, 10)
      const nextWeek = new Date(today)
      nextWeek.setDate(nextWeek.getDate() + 7)
      const nextWeekStr = nextWeek.toISOString().slice(0, 10)

      // Les journees pedagogiques ne sont pas des groupes de stage (JPA13)
      const jpa = await stagesDesJpa()

      // Current sessions: today between start_date and end_date
      const { data: currentSessions } = await sansJpa(supabase
        .from('sessions')
        .select('id, periode, semaine, start_date, end_date')
        .eq('center_id', selectedCenter)
        .eq('active', true)
        .lte('start_date', todayStr)
        .gte('end_date', todayStr)
        .order('start_date'), jpa)

      // Upcoming sessions: start_date > today, within next 7 days
      const { data: upcomingSessions } = await sansJpa(supabase
        .from('sessions')
        .select('id, periode, semaine, start_date, end_date')
        .eq('center_id', selectedCenter)
        .eq('active', true)
        .gt('start_date', todayStr)
        .lte('start_date', nextWeekStr)
        .order('start_date'), jpa)

      const allSessions = [...(currentSessions || []), ...(upcomingSessions || [])]

      // Group by periode + semaine (deduplicate)
      const groupMap = new Map<string, SessionGroup>()
      for (const s of allSessions) {
        const key = `${s.periode}|||${s.semaine}`
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            label: `${s.periode} - ${s.semaine}`,
            periode: s.periode,
            semaine: s.semaine,
            startDate: s.start_date,
            endDate: s.end_date,
          })
        }
      }

      const groups = Array.from(groupMap.values())
      setSessionGroups(groups)

      // Auto-select first group that is currently running
      if (groups.length > 0) {
        // Try to find a current session group first
        const currentGroupIndex = groups.findIndex(g => g.startDate <= todayStr && g.endDate >= todayStr)
        setSelectedSessionGroupIndex(currentGroupIndex >= 0 ? currentGroupIndex : 0)
      }
    }
    fetchSessions()
  }, [selectedCenter])

  // -----------------------------------------------------------------------
  // When session group changes: fetch kids
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedCenter || selectedSessionGroupIndex < 0 || selectedSessionGroupIndex >= sessionGroups.length) {
      return
    }

    const group = sessionGroups[selectedSessionGroupIndex]

    const fetchKidsForSessionGroup = async () => {
      setLoading(true)
      try {
        // Get session IDs for this group
        const { data: sessions } = await supabase
          .from('sessions')
          .select('id')
          .eq('center_id', selectedCenter)
          .eq('periode', group.periode)
          .eq('semaine', group.semaine)
          .eq('active', true)

        const sessionIds = (sessions || []).map((s: any) => s.id)

        if (sessionIds.length === 0) {
          setBaseKids([])
          setLoading(false)
          return
        }

        // Get kid IDs from non-cancelled registrations
        const { data: regs } = await supabase
          .from('registrations')
          .select('kid_id')
          .in('activity_id', sessionIds)
          .neq('cancellation_status', 'cancelled')

        const kidIds = [...new Set((regs || []).map((r: any) => r.kid_id))]

        if (kidIds.length === 0) {
          setBaseKids([])
          setLoading(false)
          return
        }

        // Fetch kids with full data in batches of 50
        await fetchKidsByIds(kidIds)
      } catch (err) {
        console.error('Error fetching kids for session group:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchKidsForSessionGroup()
  }, [selectedCenter, selectedSessionGroupIndex, sessionGroups])

  const fetchKidsByIds = async (kidIds: string[]) => {
    const allKids: Kid[] = []
    for (let i = 0; i < kidIds.length; i += 50) {
      const batch = kidIds.slice(i, i + 50)
      const { data, error } = await supabase
        .from('kids')
        .select(KID_SELECT)
        .in('id', batch)
        .order('nom', { ascending: true })

      if (error) throw error
      allKids.push(...(data || []).map(mapKidRow))
    }
    allKids.sort((a, b) => a.nom.localeCompare(b.nom))
    await resolveSchoolNames(allKids)
    await generatePhotoSignedUrls(allKids)
    setBaseKids(allKids)
  }

  // -----------------------------------------------------------------------
  // Client-side filters applied on displayed kids
  // -----------------------------------------------------------------------
  const displayedKids = useMemo(() => {
    let result = baseKids

    // Client-side search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim()
      result = result.filter(kid =>
        kid.nom.toLowerCase().includes(q) ||
        kid.prenom.toLowerCase().includes(q)
      )
    }

    if (hasInclusionNeeds) {
      result = result.filter(kid => kid.inclusion?.has_needs === true)
    }

    if (schoolFilter.trim()) {
      const q = schoolFilter.toLowerCase()
      result = result.filter(kid => kid.ecole?.toLowerCase().includes(q))
    }

    return result
  }, [baseKids, searchQuery, hasInclusionNeeds, schoolFilter])

  // -----------------------------------------------------------------------
  // Authorized persons on demand for detail modal
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedKid) return
    if (selectedKid.authorized_persons && selectedKid.authorized_persons.length > 0) return

    const loadAuthorizedPersons = async () => {
      const { data } = await supabase
        .from('authorized_persons')
        .select('*')
        .eq('user_id', selectedKid.user_id)

      if (data && data.length > 0) {
        setSelectedKid(prev => prev ? { ...prev, authorized_persons: data } : null)
      }
    }
    loadAuthorizedPersons()
  }, [selectedKid?.id])

  // -----------------------------------------------------------------------
  // Stats
  // -----------------------------------------------------------------------
  const stats = useMemo(() => {
    return {
      total: displayedKids.length,
      health: displayedKids.filter(kid => hasMedicalInfo(kid)).length,
      allergies: displayedKids.filter(kid => kid.allergies?.has_allergies || kid.allergies?.special_diet).length,
      inclusion: displayedKids.filter(kid => kid.inclusion?.has_needs).length,
    }
  }, [displayedKids])

  // -----------------------------------------------------------------------
  // Empty state message
  // -----------------------------------------------------------------------
  const emptyMessage = useMemo(() => {
    if (loading) return null
    if (!selectedCenter) return null // We show a different message for no center
    if (sessionGroups.length === 0 && selectedCenter) {
      return 'Aucune session en cours ou à venir pour ce centre.'
    }
    if (selectedSessionGroupIndex >= 0 && displayedKids.length === 0 && (searchQuery || hasInclusionNeeds || schoolFilter)) {
      return 'Aucun enfant ne correspond à vos critères de recherche.'
    }
    if (selectedSessionGroupIndex >= 0 && displayedKids.length === 0) {
      return 'Aucun enfant inscrit pour cette session.'
    }
    return null
  }, [loading, selectedCenter, sessionGroups.length, selectedSessionGroupIndex, displayedKids.length, searchQuery, hasInclusionNeeds, schoolFilter])

  // -----------------------------------------------------------------------
  // Helpers for session group label
  // -----------------------------------------------------------------------
  const getSessionGroupDisplayLabel = (group: SessionGroup): string => {
    return `${group.label} (${formatDate(group.startDate)} - ${formatDate(group.endDate)})`
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enfants du centre</h1>
          <p className="text-gray-600">Profils complets et informations médicales</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </button>
        </div>
      </div>

      {/* Center + Session selectors */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Center selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Centre</label>
            <select
              value={selectedCenter}
              onChange={(e) => setSelectedCenter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Sélectionnez un centre</option>
              {centers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Session group selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Session</label>
            <select
              value={selectedSessionGroupIndex}
              onChange={(e) => setSelectedSessionGroupIndex(Number(e.target.value))}
              disabled={!selectedCenter || sessionGroups.length === 0}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value={-1}>Sélectionnez une session</option>
              {sessionGroups.map((group, index) => (
                <option key={`${group.periode}-${group.semaine}`} value={index}>
                  {getSessionGroupDisplayLabel(group)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* No center selected message */}
        {!selectedCenter && (
          <div className="mt-4 text-center py-6 text-gray-500">
            <Building className="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm">Sélectionnez un centre pour voir les enfants inscrits.</p>
          </div>
        )}
      </div>

      {/* Search and Filters (only when center is selected) */}
      {selectedCenter && selectedSessionGroupIndex >= 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar — client-side only */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un enfant par nom ou prénom..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter panel */}
            {showFilters && (
              <div className="space-y-4 pt-4 border-t border-gray-200 transition-all">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={hasInclusionNeeds}
                      onChange={(e) => setHasInclusionNeeds(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Besoins spéciaux uniquement</span>
                  </label>

                  <div className="flex-1 max-w-xs">
                    <input
                      type="text"
                      placeholder="Filtrer par école..."
                      value={schoolFilter}
                      onChange={(e) => setSchoolFilter(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {(hasInclusionNeeds || schoolFilter) && (
                    <button
                      onClick={() => {
                        setHasInclusionNeeds(false)
                        setSchoolFilter('')
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {selectedCenter && selectedSessionGroupIndex >= 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total enfants</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Problèmes de santé</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.health}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Utensils className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Allergies</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.allergies}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Besoins spéciaux</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.inclusion}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && emptyMessage && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun enfant trouvé</h3>
          <p className="mt-1 text-sm text-gray-500">{emptyMessage}</p>
        </div>
      )}

      {/* Kids list */}
      {!loading && displayedKids.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Results info */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{displayedKids.length}</span> enfant{displayedKids.length > 1 ? 's' : ''} trouvé{displayedKids.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Desktop table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enfant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Âge</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">École</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Santé</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedKids.map((kid) => {
                  const healthIssues = getHealthStatus(kid)
                  const age = calculateAge(kid.date_naissance)

                  return (
                    <tr key={kid.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {kid.photo_url ? (
                              <img className="h-10 w-10 rounded-full object-cover" src={kid.photo_signed_url || kid.photo_url} alt="" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <Baby className="h-5 w-5 text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {kid.prenom} {kid.nom}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(kid.date_naissance)}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {kid.user?.prenom} {kid.user?.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {kid.user?.email}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {age} ans
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {kid.ecole || '-'}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {healthIssues.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {healthIssues.slice(0, 2).map((issue, index) => (
                              <span key={index} className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                {issue}
                              </span>
                            ))}
                            {healthIssues.length > 2 && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                +{healthIssues.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Aucun problème
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setSelectedKid(kid)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Voir le détail"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (<md) */}
          <div className="md:hidden divide-y divide-gray-200">
            {displayedKids.map((kid) => {
              const healthIssues = getHealthStatus(kid)
              const age = calculateAge(kid.date_naissance)

              return (
                <div key={kid.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 h-10 w-10">
                        {kid.photo_url ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={kid.photo_signed_url || kid.photo_url} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Baby className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {kid.prenom} {kid.nom}
                        </div>
                        <div className="text-xs text-gray-500">
                          {age} ans • {kid.ecole || 'École non renseignée'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedKid(kid)}
                      className="text-blue-600 hover:text-blue-900 transition-colors px-3 py-1 rounded-lg bg-blue-50 text-sm font-medium"
                    >
                      Voir
                    </button>
                  </div>

                  <div className="mt-2 ml-[52px]">
                    <div className="text-xs text-gray-500">
                      Parent: {kid.user?.prenom} {kid.user?.nom}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {healthIssues.length > 0 ? (
                        healthIssues.map((issue, index) => (
                          <span key={index} className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            {issue}
                          </span>
                        ))
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Aucun problème
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Kid Detail Modal */}
      {selectedKid && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-6 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                Profil de {selectedKid.prenom} {selectedKid.nom}
              </h3>
              <button
                onClick={() => setSelectedKid(null)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne 1: Informations de base */}
              <div className="space-y-6">
                {/* Photo de l'enfant */}
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  {selectedKid.photo_url ? (
                    <img
                      src={selectedKid.photo_signed_url || selectedKid.photo_url}
                      alt={`${selectedKid.prenom} ${selectedKid.nom}`}
                      className="w-24 h-24 rounded-full object-cover mx-auto mb-3"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
                      <Baby className="h-12 w-12 text-gray-500" />
                    </div>
                  )}
                  <h4 className="font-bold text-lg text-gray-900">
                    {selectedKid.prenom} {selectedKid.nom}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {calculateAge(selectedKid.date_naissance)} ans
                  </p>
                </div>

                {/* Informations personnelles */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Informations personnelles
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Date de naissance:</strong> {formatDate(selectedKid.date_naissance)} ({calculateAge(selectedKid.date_naissance)} ans)</div>
                    <div><strong>Numéro national:</strong> {selectedKid.n_national || 'Non renseigné'}</div>
                    <div><strong>École:</strong> {selectedKid.ecole || 'Non renseignée'}</div>
                    {selectedKid.adresse && (
                      <div><strong>Adresse:</strong> {selectedKid.adresse}, {selectedKid.cpostal} {selectedKid.localite}</div>
                    )}
                  </div>
                </div>

                {/* Contact parent */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact parent
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Nom:</strong> {selectedKid.user?.prenom} {selectedKid.user?.nom}</div>
                    <div><strong>Email:</strong> {selectedKid.user?.email}</div>
                    <div><strong>Téléphone:</strong> {selectedKid.user?.telephone || 'Non renseigné'}</div>
                  </div>
                </div>
              </div>

              {/* Colonne 2: Informations médicales */}
              <div className="space-y-6">
                {/* Health */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Heart className="h-4 w-4 mr-2" />
                    Santé
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Problèmes médicaux:</strong> {selectedKid.health?.specific_medical || 'Aucun'}</div>
                    <div><strong>Médicaments:</strong> {selectedKid.health?.medication ? 'Oui' : 'Non'}</div>
                    {selectedKid.health?.medication && (
                      <div><strong>Détails médicaments:</strong> {selectedKid.health.medication_details}</div>
                    )}
                    <div><strong>Tétanos à jour:</strong> {selectedKid.health?.tetanus ? 'Oui' : 'Non'}</div>
                    {selectedKid.health?.doctor_name && (
                      <div><strong>Médecin:</strong> {selectedKid.health.doctor_name}</div>
                    )}
                    {selectedKid.health?.doctor_phone && (
                      <div><strong>Tél. médecin:</strong> {selectedKid.health.doctor_phone}</div>
                    )}
                  </div>
                </div>

                {/* Allergies */}
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Utensils className="h-4 w-4 mr-2" />
                    Allergies et régime
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Allergies:</strong> {selectedKid.allergies?.has_allergies ? 'Oui' : 'Non'}</div>
                    {selectedKid.allergies?.has_allergies && (
                      <div><strong>Détails:</strong> {selectedKid.allergies.allergies_details}</div>
                    )}
                    <div><strong>Régime spécial:</strong> {selectedKid.allergies?.special_diet ? 'Oui' : 'Non'}</div>
                    {selectedKid.allergies?.special_diet && (
                      <div><strong>Détails régime:</strong> {selectedKid.allergies.diet_details}</div>
                    )}
                  </div>
                </div>

                {/* Activities */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Activity className="h-4 w-4 mr-2" />
                    Activités
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Peut participer:</strong> {selectedKid.activities?.can_participate ? 'Oui' : 'Non'}</div>
                    <div><strong>Sait nager:</strong> {selectedKid.activities?.can_swim ? 'Oui' : 'Non'}</div>
                    {selectedKid.activities?.can_swim && (
                      <div><strong>Niveau natation:</strong> {selectedKid.activities.swim_level || 'Non précisé'}</div>
                    )}
                    {selectedKid.activities?.restriction_details && (
                      <div><strong>Restrictions:</strong> {selectedKid.activities.restriction_details}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Colonne 3: Personnes autorisées et inclusion */}
              <div className="space-y-6">
                {/* Personnes autorisées */}
                {selectedKid.authorized_persons && selectedKid.authorized_persons.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Personnes autorisées ({selectedKid.authorized_persons.length})
                    </h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedKid.authorized_persons.map((person) => (
                        <div key={person.id} className="bg-white p-3 rounded border border-yellow-200">
                          <div className="flex items-start space-x-3">
                            {person.photo_url ? (
                              <img
                                src={person.photo_url}
                                alt={`${person.first_name} ${person.last_name}`}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <User className="h-5 w-5 text-gray-500" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {person.first_name} {person.last_name}
                              </div>
                              <div className="text-gray-600 text-sm">
                                {person.relationship}
                              </div>
                              <div className="flex items-center text-gray-500 mt-1 text-sm">
                                <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{person.phone_number}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inclusion */}
                {selectedKid.inclusion && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      Inclusion et besoins spéciaux
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>A des besoins spéciaux:</strong> {selectedKid.inclusion.has_needs ? 'Oui' : 'Non'}</div>
                      {(selectedKid.inclusion.has_needs || selectedKid.inclusion.niveau_acces != null) && (
                        <NiveauDAcces
                          key={selectedKid.id}
                          kidId={selectedKid.id}
                          prenom={selectedKid.prenom}
                          decision={selectedKid.inclusion}
                          onDecide={decision => {
                            const maj = (k: Kid) => (k.id === selectedKid.id && k.inclusion ? { ...k, inclusion: { ...k.inclusion, ...decision } } : k)
                            setSelectedKid(prev => (prev ? maj(prev) : prev))
                            setBaseKids(prev => prev.map(maj))
                          }}
                        />
                      )}
                      {selectedKid.inclusion.has_needs && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {selectedKid.inclusion.situation_details && (
                            <div><strong>Description de la situation:</strong> {selectedKid.inclusion.situation_details}</div>
                          )}
                          {selectedKid.inclusion.impact_details && (
                            <div><strong>Impact sur les activités:</strong> {selectedKid.inclusion.impact_details}</div>
                          )}
                          {selectedKid.inclusion.needs_dedicated_staff && (
                            <div><strong>Personnel dédié nécessaire:</strong> {selectedKid.inclusion.needs_dedicated_staff}</div>
                          )}
                          {selectedKid.inclusion.staff_details && (
                            <div><strong>Détails personnel:</strong> {selectedKid.inclusion.staff_details}</div>
                          )}
                          {selectedKid.inclusion.strategies && (
                            <div><strong>Stratégies d'accompagnement:</strong> {selectedKid.inclusion.strategies}</div>
                          )}
                          {selectedKid.inclusion.assistive_devices && (
                            <div><strong>Dispositifs d'aide:</strong> {selectedKid.inclusion.assistive_devices}</div>
                          )}
                          {selectedKid.inclusion.stress_signals && (
                            <div><strong>Signaux de stress:</strong> {selectedKid.inclusion.stress_signals}</div>
                          )}
                          {selectedKid.inclusion.strengths && (
                            <div><strong>Points forts:</strong> {selectedKid.inclusion.strengths}</div>
                          )}
                          {selectedKid.inclusion.previous_experience && (
                            <div><strong>Expérience précédente:</strong> {selectedKid.inclusion.previous_experience}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Departure */}
                {selectedKid.departure && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Départ
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Part seul:</strong> {selectedKid.departure.leaves_alone ? 'Oui' : 'Non'}</div>
                      {selectedKid.departure.departure_time && (
                        <div><strong>Heure de départ:</strong> {selectedKid.departure.departure_time}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Statistiques */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Inscriptions
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Inscriptions actives:</strong> {selectedKid.active_registrations_count || 0}</div>
                    <div><strong>Total inscriptions:</strong> {selectedKid.registrations_count || 0}</div>
                    <div><strong>Inscrit depuis:</strong> {formatDate(selectedKid.created_at)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3 border-t border-gray-200 pt-4">
              <button
                onClick={() => setSelectedKid(null)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
